// Table diff for a pair of table-wrapper blocks (RES-561 compare feature).
//
// A table's cells are a FLAT child list keyed by row/col props (see
// @threeskye/editor-component-slate tables.js addTable), so cells are matched by
// their 'row,col' key per side — never by array position. Presentation props
// (columnWidths, rowStyles, colStyles, styles, columnJustify) are ignored entirely.

import diffText from './diffText';
import { markSubtree } from './annotate';
import { normalisedEqual } from './normalise';

function findChild(wrapper, type) {
	return (wrapper.children || []).find((c) => c && c.type === type);
}

function cellMap(table) {
	const map = new Map();
	(table.children || []).forEach((c) => {
		if (c && c.type === 'table-cell')
			map.set(c.row + ',' + c.col, c);
	});
	return map;
}

function cellKeyCompare(a, b) {
	const [ar, ac] = a.split(',').map(Number);
	const [br, bc] = b.split(',').map(Number);
	return ar - br || ac - bc;
}

function markPairChanged(leftNode, rightNode, ctx) {
	const hunkId = ctx.newHunk('changed');
	markSubtree(leftNode, 'diffRemoved', hunkId, 'changed');
	markSubtree(rightNode, 'diffAdded', hunkId, 'changed');
}

// Annotates both wrappers in place (they are clones); returns true when anything
// inside the table differs. diffBlockList is passed in from diffSlate (cell contents
// are ordinary block lists) to avoid a circular import.
export default function diffTable(leftWrapper, rightWrapper, ctx, diffBlockList) {
	const hunksBefore = ctx.hunks.length;

	['table-header', 'table-footer'].forEach((type) => {
		const l = findChild(leftWrapper, type);
		const r = findChild(rightWrapper, type);
		if (l && r) {
			const dt = diffText(l.children, r.children, ctx);
			if (dt) {
				l.children = dt.leftChildren;
				r.children = dt.rightChildren;
			} else if (!normalisedEqual(l, r)) {
				markPairChanged(l, r, ctx);
			}
		} else if (l) {
			markSubtree(l, 'diffRemoved', ctx.newHunk('removed'));
		} else if (r) {
			markSubtree(r, 'diffAdded', ctx.newHunk('added'));
		}
	});

	const leftTable = findChild(leftWrapper, 'table');
	const rightTable = findChild(rightWrapper, 'table');
	if (leftTable && rightTable) {
		const leftCells = cellMap(leftTable);
		const rightCells = cellMap(rightTable);
		const keys = [...new Set([...leftCells.keys(), ...rightCells.keys()])].sort(cellKeyCompare);
		keys.forEach((key) => {
			const lc = leftCells.get(key);
			const rc = rightCells.get(key);
			if (lc && rc) {
				if ((lc.colSpan || 1) !== (rc.colSpan || 1) || (lc.rowSpan || 1) !== (rc.rowSpan || 1))
					markPairChanged(lc, rc, ctx);
				else
					diffBlockList(lc.children, rc.children, ctx);
			} else if (lc) {
				markSubtree(lc, 'diffRemoved', ctx.newHunk('removed'));
			} else {
				markSubtree(rc, 'diffAdded', ctx.newHunk('added'));
			}
		});
	} else if (leftTable) {
		markSubtree(leftTable, 'diffRemoved', ctx.newHunk('removed'));
	} else if (rightTable) {
		markSubtree(rightTable, 'diffAdded', ctx.newHunk('added'));
	}

	return ctx.hunks.length > hunksBefore;
}
